/**
 * MijnKledaView: alles wat een klant met Kleda heeft op één pagina
 * (?type=mijn-kleda, plan 2026-10-26 golf C).
 *
 * Secties: volgende reiniging (kleed-abonnement, of een herinnering die kantoor
 * zette, met overslaan en stoppen), je kleden met "Bestel opnieuw" (richtprijs, één bevestiging,
 * dan de offertepagina), je bestellingen (nummer, datum, status; geen adres,
 * factuur of betaallink van andere orders), je gegevens, berichten
 * (herinneringen voor je kleed aan of uit) en hulp.
 *
 * Bereikbaar op het token van een order (link in elke mail). De server doet
 * alles: dit scherm toont en stuurt acties door.
 *
 * Niveau (plan 2026-10-28): basis = via het ordertoken; volledig = ingelogd
 * via een inloglink (cookie, de server beslist). De kop zegt waar je bent en
 * biedt inloggen of uitloggen aan.
 *
 * Je gegevens (golf 2): GegevensKaart, met telefoon, adres en e-mail zelf
 * wijzigen voor zover data.gegevens dat toestaat.
 */
import { useEffect, useState } from 'react'
import { KLEDA_CONTACT } from '@platform/kleda-content'
import { fetchMijnKleda, postMijnKleda, uitloggen, type AbonnementStand, type MijnKledaData } from '../api'
import { trackBestelOpnieuwClicked, trackMailvoorkeurChanged, trackMijnKledaUitgelogd, trackMijnKledaViewed } from '../analytics'
import { Card, StatusHeader } from './UI'
import { VolgendeKeerKaart, ritmeZin } from './VolgendeKeerKaart'
import { GegevensKaart } from './mijn-kleda/GegevensKaart'

interface Props {
  token: string
  werkOrderId: string
  heeftMobiel?: boolean
}

const INLOG_AAN = import.meta.env.VITE_MIJN_KLEDA_INLOG_ENABLED === 'true'

function datumNl(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`)
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : iso
}

function euro(n: number): string {
  return n.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })
}

function SectieKop({ children }: { children: string }) {
  return <h2 className="mt-lg mb-sm text-[13px] font-semibold uppercase tracking-[0.5px] text-ink-faint">{children}</h2>
}

/** Overslaan of stoppen; de melding gaat naar de ouder, die de stand opnieuw laadt (anders verdwijnt ze met deze kaart). */
function AbonnementBeheer({ token, abonnement, onGewijzigd }: { token: string; abonnement: AbonnementStand; onGewijzigd: (melding: string) => void }) {
  const [bevestig, setBevestig] = useState<'overslaan' | 'stop' | null>(null)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)
  const vandaag = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' })
  const teLaat = vandaag > abonnement.overslaanTot

  const voerUit = async (actie: 'overslaan' | 'stop') => {
    setBezig(true)
    setFout(null)
    const r = await postMijnKleda({ token, actie })
    setBezig(false)
    setBevestig(null)
    if (!r.success || !r.data) {
      setFout(r.error?.message || 'Dat lukte niet. Probeer het zo opnieuw of bel ons.')
      return
    }
    const d = r.data
    if (d.actie === 'overslaan') {
      onGewijzigd(d.uitkomst === 'overgeslagen'
        ? `Geregeld, we slaan deze keer over. Je volgende reiniging staat nu rond ${datumNl(d.nextRunAt)}.`
        : 'Je sloeg twee keer achter elkaar over, dus we hebben je abonnement gestopt. Geen kosten.')
    } else if (d.actie === 'stop') {
      onGewijzigd('Je kleed-abonnement is gestopt. Geen kosten, geen vragen.')
    }
  }

  return (
    <Card>
      <p className="text-body font-medium text-ink mb-xs">Je kleed-abonnement: {ritmeZin(abonnement.intervalMaanden)}</p>
      <p className="text-body-sm text-ink-muted">
        Je volgende reiniging staat rond {datumNl(abonnement.nextRunAt)}, met {abonnement.kortingPct}% korting.
        {teLaat
          ? ' Je order voor deze keer staat klaar; kies je ophaaldag op de pagina van die bestelling.'
          : ` Tot en met ${datumNl(abonnement.overslaanTot)} kun je deze keer overslaan of stoppen.`}
      </p>
      {!teLaat && bevestig === null && (
        <div className="mt-sm flex flex-wrap gap-md">
          <button type="button" onClick={() => setBevestig('overslaan')} className="text-body-sm font-medium text-primary-dark underline">
            Deze keer overslaan
          </button>
          <button type="button" onClick={() => setBevestig('stop')} className="text-body-sm text-ink-muted underline">
            Abonnement stoppen
          </button>
        </div>
      )}
      {bevestig === 'overslaan' && (
        <div className="mt-sm rounded-xl bg-surface-soft p-sm">
          <p className="text-body-sm text-ink mb-sm">
            {abonnement.overslaanMag
              ? `Dan maken we nu geen order aan en schuift je volgende reiniging ${abonnement.intervalMaanden} maanden op. Je korting blijft staan.`
              : 'Je sloeg de vorige keer al over. Nog een keer overslaan betekent dat je abonnement stopt.'}
          </p>
          <div className="flex flex-wrap gap-sm">
            <button type="button" disabled={bezig} onClick={() => void voerUit('overslaan')}
              className="rounded-xl bg-ink px-md py-sm text-body-sm font-semibold text-white disabled:opacity-40">
              {abonnement.overslaanMag ? 'Ja, deze keer overslaan' : 'Ja, overslaan en stoppen'}
            </button>
            <button type="button" disabled={bezig} onClick={() => setBevestig(null)} className="text-body-sm text-ink-muted underline">Toch niet</button>
          </div>
        </div>
      )}
      {bevestig === 'stop' && (
        <div className="mt-sm rounded-xl bg-surface-soft p-sm">
          <p className="text-body-sm text-ink mb-sm">Dan maken we geen orders meer aan. Geen kosten, geen vragen. Een losse reiniging kan altijd.</p>
          <div className="flex flex-wrap gap-sm">
            <button type="button" disabled={bezig} onClick={() => void voerUit('stop')}
              className="rounded-xl bg-ink px-md py-sm text-body-sm font-semibold text-white disabled:opacity-40">
              Ja, stop mijn abonnement
            </button>
            <button type="button" disabled={bezig} onClick={() => setBevestig(null)} className="text-body-sm text-ink-muted underline">Toch niet</button>
          </div>
        </div>
      )}
      {fout && <p className="mt-sm text-body-sm text-red-700">{fout}</p>}
    </Card>
  )
}

export function MijnKledaView({ token, werkOrderId, heeftMobiel = false }: Props) {
  const [data, setData] = useState<MijnKledaData | null>(null)
  const [fout, setFout] = useState<string | null>(null)
  const [laden, setLaden] = useState(true)
  const [versie, setVersie] = useState(0)
  const [bestelStap, setBestelStap] = useState<'dicht' | 'bevestig' | 'bezig'>('dicht')
  const [bestelFout, setBestelFout] = useState<string | null>(null)
  const [mailBezig, setMailBezig] = useState(false)
  const [mailFout, setMailFout] = useState<string | null>(null)
  const [uitloggenBezig, setUitloggenBezig] = useState(false)
  /** Uitkomst van overslaan of stoppen; blijft staan terwijl de stand opnieuw laadt. */
  const [abonnementMelding, setAbonnementMelding] = useState<string | null>(null)
  /** Uitkomst van een gegevenswijziging; idem. */
  const [gegevensMelding, setGegevensMelding] = useState<string | null>(null)

  useEffect(() => {
    let actief = true
    fetchMijnKleda(token).then((r) => {
      if (!actief) return
      setLaden(false)
      if (!r.success || !r.data) {
        setFout(r.error?.message || 'Mijn Kleda is nu niet te laden.')
        return
      }
      setData(r.data)
      if (versie === 0) trackMijnKledaViewed(werkOrderId, r.data.niveau)
    })
    return () => { actief = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, versie])

  if (laden) return <p className="text-body-sm text-ink-muted">Even laden...</p>
  if (fout || !data) {
    return (
      <Card>
        <p className="text-body-sm text-ink">{fout ?? 'Mijn Kleda is nu niet te laden.'}</p>
        <p className="mt-sm text-body-sm text-ink-muted">
          Bel <a href={`tel:${KLEDA_CONTACT.telefoonTel}`} className="underline">{KLEDA_CONTACT.telefoonWeergave}</a> of mail{' '}
          <a href={`mailto:${KLEDA_CONTACT.email}`} className="underline">{KLEDA_CONTACT.email}</a>.
        </p>
      </Card>
    )
  }

  const bestelOpnieuw = async () => {
    setBestelStap('bezig')
    setBestelFout(null)
    trackBestelOpnieuwClicked(werkOrderId)
    const r = await postMijnKleda({ token, actie: 'bestel-opnieuw' })
    if (r.success && r.data && r.data.actie === 'bestel-opnieuw') {
      window.location.href = r.data.url
      return
    }
    setBestelStap('bevestig')
    setBestelFout(r.error?.message || 'Dat lukte niet. Probeer het zo opnieuw of bel ons.')
  }

  const zetMail = async (aan: boolean) => {
    setMailBezig(true)
    setMailFout(null)
    const r = await postMijnKleda({ token, actie: 'mailvoorkeur', aan })
    setMailBezig(false)
    if (r.success) {
      setData({ ...data, mailvoorkeur: { aan } })
      trackMailvoorkeurChanged(werkOrderId, aan)
    } else {
      setMailFout(r.error?.message || 'Dat lukte niet. Probeer het zo opnieuw of bel ons.')
    }
  }

  const loguit = async () => {
    setUitloggenBezig(true)
    trackMijnKledaUitgelogd(werkOrderId)
    await uitloggen()
    window.location.reload()
  }

  const { klant, volgendeReiniging } = data
  const huidigHref = `/?token=${encodeURIComponent(token)}`
  const mailvoorkeurAan = data.mailvoorkeur.aan

  return (
    <div>
      <StatusHeader
        title="Mijn Kleda"
        subtitle={<>Hoi{klant.voornaam ? ` ${klant.voornaam}` : ''}, hier staat alles wat je met ons hebt: je kleden, je volgende reiniging en je bestellingen.</>}
      />
      {data.niveau === 'volledig' ? (
        <p className="-mt-sm mb-md text-[13px] text-ink-faint">
          Ingelogd{klant.email ? <> als <span className="break-all">{klant.email}</span></> : ''}.{' '}
          <button type="button" disabled={uitloggenBezig} onClick={() => void loguit()} className="underline disabled:opacity-40">Uitloggen</button>
        </p>
      ) : INLOG_AAN ? (
        <p className="-mt-sm mb-md text-[13px] text-ink-faint">
          Volgende keer geen mail bij de hand? <a href="/inloggen" className="underline">Log in met je e-mailadres</a>, zonder wachtwoord.
        </p>
      ) : null}

      <SectieKop>Volgende reiniging</SectieKop>
      {abonnementMelding && (
        <Card className="mb-sm">
          <p className="text-body-sm text-ink">{abonnementMelding} De bevestiging komt per mail.</p>
        </Card>
      )}
      {volgendeReiniging.abonnement ? (
        <AbonnementBeheer token={token} abonnement={volgendeReiniging.abonnement} onGewijzigd={(m) => { setAbonnementMelding(m); setVersie((v) => v + 1) }} />
      ) : abonnementMelding ? null : (
        <VolgendeKeerKaart
          token={token}
          werkOrderId={werkOrderId}
          heeftMobiel={heeftMobiel}
          bron="mijn-kleda"
          standalone
          verbergMijnKledaLink
          leegTekst={data.bestelOpnieuwMogelijk
            ? 'Wil je je kleed weer laten reinigen? Met "Bestel opnieuw" hieronder staat je offerte zo klaar.'
            : 'Zodra je kleed bij ons is geweest, kies je hier wanneer het weer aan de beurt is.'}
          className=""
        />
      )}

      {data.kleden.length > 0 && (
        <>
          <SectieKop>Je kleden</SectieKop>
          <Card>
            <ul className="space-y-xs">
              {data.kleden.map((k, i) => (
                <li key={i} className="text-body-sm text-ink">{k.omschrijving}</li>
              ))}
            </ul>
            {data.richtprijs && (
              <p className="mt-sm text-body-sm text-ink-muted">
                Richtprijs met {data.richtprijs.pakketLabel}: <span className="font-semibold text-ink">{euro(data.richtprijs.bedrag)}</span>, inclusief gratis ophalen en bezorgen.
              </p>
            )}
            {data.bestelOpnieuwMogelijk && bestelStap === 'dicht' && (
              <button type="button" onClick={() => setBestelStap('bevestig')}
                className="mt-md rounded-xl bg-ink px-md py-sm text-body-sm font-semibold text-white">
                Bestel opnieuw
              </button>
            )}
            {data.bestelOpnieuwMogelijk && bestelStap !== 'dicht' && (
              <div className="mt-md rounded-xl bg-surface-soft p-sm">
                <p className="text-body-sm text-ink mb-sm">
                  We zetten een offerte klaar met de maten van vorige keer en de prijs van nu. In de wasserij meten we precies na. Daarna kies jij de ophaaldag.
                </p>
                <div className="flex flex-wrap gap-sm">
                  <button type="button" disabled={bestelStap === 'bezig'} onClick={() => void bestelOpnieuw()}
                    className="rounded-xl bg-ink px-md py-sm text-body-sm font-semibold text-white disabled:opacity-40">
                    {bestelStap === 'bezig' ? 'Even geduld...' : 'Ja, zet mijn offerte klaar'}
                  </button>
                  <button type="button" disabled={bestelStap === 'bezig'} onClick={() => setBestelStap('dicht')} className="text-body-sm text-ink-muted underline">
                    Toch niet
                  </button>
                </div>
                {bestelFout && <p className="mt-sm text-body-sm text-red-700">{bestelFout}</p>}
              </div>
            )}
            <p className="mt-sm text-[13px] text-ink-faint">
              Nieuw kleed of andere maten? <a href="https://boeken.kleda.nl" className="underline">Bereken je prijs</a>.
            </p>
          </Card>
        </>
      )}

      {data.bestellingen.length > 0 && (
        <>
          <SectieKop>Je bestellingen</SectieKop>
          <div className="bg-white rounded-premium border border-track shadow-card overflow-hidden divide-y divide-hairline">
            {data.bestellingen.map((b) => (
              <div key={b.nummer} className="flex items-center gap-sm px-md py-sm">
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] text-ink-soft">{b.nummer}{b.datum ? ` · ${datumNl(b.datum)}` : ''}</span>
                  <span className="block text-[12px] text-ink-faint">
                    {b.statusLabel}{b.aantalKleden ? ` · ${b.aantalKleden} ${b.aantalKleden === 1 ? 'kleed' : 'kleden'}` : ''}
                  </span>
                </span>
                {b.isHuidig && (
                  <a href={huidigHref} className="text-[13px] font-bold whitespace-nowrap text-primary">Bekijken →</a>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <SectieKop>Je gegevens</SectieKop>
      {gegevensMelding && (
        <Card className="mb-sm">
          <p className="text-body-sm text-ink">{gegevensMelding}</p>
        </Card>
      )}
      <GegevensKaart
        token={token}
        werkOrderId={werkOrderId}
        data={data}
        onGewijzigd={(m) => { setGegevensMelding(m); setVersie((v) => v + 1) }}
      />

      <SectieKop>Berichten</SectieKop>
      {mailvoorkeurAan === null ? (
        <Card>
          <p className="text-body-sm text-ink-muted">Je krijgt van ons alleen berichten over je bestelling.</p>
        </Card>
      ) : (
          <Card>
            <div className="flex items-start gap-md">
              <div className="flex-1">
                <p className="text-body font-medium text-ink">Herinneringen voor je kleed</p>
                <p className="text-body-sm text-ink-muted">
                  Alleen herinneringen en tips voor je kleed. Berichten over je bestelling krijg je altijd.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={mailvoorkeurAan}
                disabled={mailBezig}
                onClick={() => void zetMail(!mailvoorkeurAan)}
                className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${mailvoorkeurAan ? 'bg-ink' : 'bg-track'}`}
              >
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${mailvoorkeurAan ? 'left-6' : 'left-1'}`} />
                <span className="sr-only">{mailvoorkeurAan ? 'Aan' : 'Uit'}</span>
              </button>
            </div>
            {mailFout && <p className="mt-sm text-body-sm text-red-700">{mailFout}</p>}
          </Card>
      )}

      <SectieKop>Hulp</SectieKop>
      <Card>
        <p className="text-body-sm text-ink-muted">
          Vragen? Bel <a href={`tel:${KLEDA_CONTACT.telefoonTel}`} className="underline">{KLEDA_CONTACT.telefoonWeergave}</a> ({KLEDA_CONTACT.klantenservice}) of mail{' '}
          <a href={`mailto:${KLEDA_CONTACT.email}`} className="underline">{KLEDA_CONTACT.email}</a>.
        </p>
        <p className="mt-sm text-body-sm text-ink-muted">
          Blij met je kleed? <a href={data.reviewLink} target="_blank" rel="noopener" className="underline">Laat een review achter op Google</a>.
        </p>
      </Card>

      <p className="mt-lg">
        <a href={huidigHref} className="text-body-sm text-ink-muted underline">Terug naar je bestelling</a>
      </p>
    </div>
  )
}
